import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {omit} from 'ramda';

import {DateBox as DXDateBox} from 'devextreme-react';


export default class DateBox extends Component {
    constructor(props) {
        super(props);

        this.state = {
            value: props.value,
            opened: props.opened,
            disabled: props.disabled,
        };

        this.onOptionChanged = this.onOptionChanged.bind(this);
        this.onValueChanged = this.onValueChanged.bind(this);
    }

    UNSAFE_componentWillReceiveProps(props) {
        this.setState({
            value: props.value,
            opened: props.opened,
            disabled: props.disabled,
        });
    }

    onValueChanged(e) {
        const {setProps} = this.props;
        if (setProps) {
            setProps({
                value: e.value,
                valueChanged: {value: e.value, previousValue: e.previousValue, timestamp: Date.now()}
            });
        }
        this.setState({value: e.value});
    }

    onOptionChanged(e) {
        if (e.name === 'opened') {
            this.setState({opened: !this.state.opened});
        }
    }

    render() {
        return (
            <DXDateBox
                {...omit(['setProps'], this.props)}
                opened={this.state.opened}
                value={this.state.value}
                disabled={this.state.disabled}
                onOptionChanged={this.props.onOptionChanged || this.onOptionChanged}
                onValueChanged={this.props.onValueChanged || this.onValueChanged}
            />
        );
    }
}

DateBox.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks
     */
    id: PropTypes.string,

    /**
     * Specifies whether or not the UI component allows an end-user to enter a custom value
     */
    acceptCustomValue: PropTypes.bool,
    /**
     * Specifies the shortcut key that sets focus on the UI component
     */
    accessKey: PropTypes.string,
    /**
     * Specifies whether or not the UI component changes its state when interacting with a user
     */
    activeStateEnabled: PropTypes.bool,
    /**
     * Specifies whether or not adaptive UI component rendering is enabled on a small screen
     */
    adaptivityEnabled: PropTypes.bool,
    /**
     * The text displayed on the Apply button
     */
    applyButtonText: PropTypes.string,
    /**
     * Specifies the way an end-user applies the selected value
     */
    applyValueMode: PropTypes.oneOf(['instantly', 'useButtons']),
    /**
     * Allows you to add custom buttons to the input text field
     */
    buttons: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    /**
     * Configures the calendar's value picker. Applies only if the pickerType is "calendar"
     */
    calendarOptions: PropTypes.object,
    /**
     * The text displayed on the Cancel button
     */
    cancelButtonText: PropTypes.string,
    /**
     * Specifies the message displayed if the specified date is later than the max value or earlier than the min value
     */
    dateOutOfRangeMessage: PropTypes.string,
    /**
     * Specifies the date-time value serialization format. Use it only if you do not specify the value at design time
     */
    dateSerializationFormat: PropTypes.string,
    /**
     * Specifies whether to render the drop-down field's content when it is displayed. If false, the content is rendered immediately
     */
    deferRendering: PropTypes.bool,
    /**
     * Specifies whether the UI component responds to user interaction
     */
    disabled: PropTypes.bool,
    /**
     * Specifies dates that users cannot select. Applies only if pickerType is "calendar"
     */
    disabledDates: PropTypes.oneOfType([PropTypes.arrayOf(Date), PropTypes.func]),
    /**
     * Specifies the date display format. Ignored if the pickerType property is "native"
     */
    displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    /**
     * An alias for the dropDownButtonTemplate property specified in React. Accepts a custom component. Refer to Using a Custom Component for more information.
     */
    dropDownButtonComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    /**
     * An alias for the dropDownButtonTemplate property specified in React. Accepts a rendering function. Refer to Using a Rendering Function for more information.
     */
    dropDownButtonRender: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * Specifies a custom template for the drop-down button
     */
    dropDownButtonTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    /**
     * Configures the drop-down field which holds the content
     */
    dropDownOptions: PropTypes.object,
    /**
     * Specifies the global attributes to be attached to the UI component's container element
     */
    elementAttr: PropTypes.object,
    /**
     * Specifies whether the UI component can be focused using keyboard navigation
     */
    focusStateEnabled: PropTypes.bool,
    /**
     * Specifies the UI component's height
     */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component
     */
    hint: PropTypes.string,
    /**
     * Specifies whether the UI component changes its state when a user pauses on it
     */
    hoverStateEnabled: PropTypes.bool,
    /**
     * Specifies the attributes to be passed on to the underlying HTML element
     */
    inputAttr: PropTypes.object,
    /**
     * Specifies the interval between neighboring values in the popup list in minutes
     */
    interval: PropTypes.number,
    /**
     * Specifies the message displayed if the typed value is not a valid date or time
     */
    invalidDateMessage: PropTypes.string,
    /**
     * Specifies or indicates whether the editor's value is valid
     */
    isValid: PropTypes.bool,
    /**
     * The last date that can be selected within the UI component
     */
    max: PropTypes.oneOfType([Date, PropTypes.number, PropTypes.string]),
    /**
     * Specifies the maximum number of characters you can enter into the textbox
     */
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * The minimum date that can be selected within the UI component
     */
    min: PropTypes.oneOfType([Date, PropTypes.number, PropTypes.string]),
    /**
     * The value to be assigned to the name attribute of the underlying HTML element
     */
    name: PropTypes.string,
    /**
     * A function that is executed when the UI component loses focus after the text field's content was changed using the keyboard
     */
    onChange: PropTypes.func,
    /**
     * A function that is executed once the drop-down editor is closed
     */
    onClosed: PropTypes.func,
    /**
     * A function that is executed when the UI component's content is ready and each time the content is changed
     */
    onContentReady: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been copied
     */
    onCopy: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been cut
     */
    onCut: PropTypes.func,
    /**
     * A function that is executed before the UI component is disposed of
     */
    onDisposing: PropTypes.func,
    /**
     * A function that is executed when the Enter key has been pressed while the UI component is focused
     */
    onEnterKey: PropTypes.func,
    /**
     * A function that is executed when the UI component gets focus
     */
    onFocusIn: PropTypes.func,
    /**
     * A function that is executed when the UI component loses focus
     */
    onFocusOut: PropTypes.func,
    /**
     * A function used in JavaScript frameworks to save the UI component instance
     */
    onInitialized: PropTypes.func,
    /**
     * A function that is executed each time the UI component's input is changed while the UI component is focused
     */
    onInput: PropTypes.func,
    /**
     * A function that is executed when a user is pressing a key on the keyboard
     */
    onKeyDown: PropTypes.func,
    /**
     * A function that is executed when a user releases a key on the keyboard
     */
    onKeyUp: PropTypes.func,
    /**
     * A function that is executed once the drop-down editor is opened
     */
    onOpened: PropTypes.func,
    /**
     * A function that is executed after a UI component property is changed
     */
    onOptionChanged: PropTypes.func,
    /**
     * A function that is executed when the UI component's input has been pasted
     */
    onPaste: PropTypes.func,
    /**
     * A function that is executed after the UI component's value is changed
     */
    onValueChanged: PropTypes.func,
    /**
     * Specifies whether or not the drop-down editor is displayed
     */
    opened: PropTypes.bool,
    /**
     * Specifies whether a user can open the drop-down list by clicking a text field
     */
    openOnFieldClick: PropTypes.bool,
    /**
     * Specifies the type of the date/time picker.
     */
    pickerType: PropTypes.oneOf(['calendar', 'list', 'native', 'rollers']),
    /**
     * The text displayed by the UI component when the UI component value is empty
     */
    placeholder: PropTypes.string,
    /**
     * Specifies whether the editor is read-only
     */
    readOnly: PropTypes.bool,
    /**
     * Switches the UI component to a right-to-left representation
     */
    rtlEnabled: PropTypes.bool,
    /**
     * Specifies whether to show the analog clock in the value picker. Applies only if type is "datetime" and pickerType is "calendar".
     */
    showAnalogClock: PropTypes.bool,
    /**
     * Specifies whether to display the Clear button in the UI component
     */
    showClearButton: PropTypes.bool,
    /**
     * Specifies whether the drop-down button is visible
     */
    showDropDownButton: PropTypes.bool,
    /**
     * Specifies whether or not the UI component checks the inner text for spelling mistakes
     */
    spellcheck: PropTypes.bool,
    /**
     * Specifies how the UI component's text field is styled
     */
    stylingMode: PropTypes.oneOf(['outlined', 'underlined', 'filled']),
    /**
     * Specifies the number of the element when the Tab key is used for navigating
     */
    tabIndex: PropTypes.number,
    /**
     * A format used to display date/time information
     */
    type: PropTypes.oneOf(['date', 'datetime', 'time']),
    /**
     * Specifies whether to control user input using a mask created based on the displayFormat
     */
    useMaskBehavior: PropTypes.bool,
    /**
     * Information on the broken validation rule. Contains the first item from the validationErrors array
     */
    validationError: PropTypes.object,
    /**
     * An array of the validation rules that failed
     */
    validationErrors: PropTypes.object,
    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor's value is displayed
     */
    validationMessageMode: PropTypes.oneOf(['always', 'auto']),
    /**
     * Indicates or specifies the current validation status
     */
    validationStatus: PropTypes.oneOf(['valid', 'invalid', 'pending']),
    /**
     * An object or a value specifying the date and time currently selected using the date box
     */
    value: PropTypes.oneOfType([Date, PropTypes.number, PropTypes.string, PropTypes.object]),
    /**
     * Dash event
     */
    valueChanged: PropTypes.object,
    /**
     * Specifies the DOM events after which the UI component's value should be updated
     */
    valueChangeEvent: PropTypes.string,
    /**
     * Specifies whether the UI component is visible
     */
    visible: PropTypes.bool,
    /**
     * Specifies the UI component's width
     */
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),

    /**
     * Dash-assigned callback that gets fired when the value changes.
     */
    setProps: PropTypes.func,
};

DateBox.defaultProps = {
    acceptCustomValue: true,
    accessKey: null,
    activeStateEnabled: false,
    adaptivityEnabled: false,
    applyButtonText: 'OK',
    applyValueMode: 'instantly',
    // buttons: undefined,
    calendarOptions: {},
    cancelButtonText: 'Cancel',
    dateOutOfRangeMessage: 'Value is out of range',
    // dateSerializationFormat: undefined,
    deferRendering: true,
    disabled: false,
    disabledDates: null,
    displayFormat: null,
    dropDownButtonComponent: null,
    dropDownButtonRender: null,
    dropDownButtonTemplate: 'dropDownButton',
    dropDownOptions: {},
    elementAttr: {},
    focusStateEnabled: true,
    height: undefined,
    hint: undefined,
    hoverStateEnabled: true,
    inputAttr: {},
    interval: 30,
    invalidDateMessage: 'Value must be a date or time',
    isValid: true,
    // max: undefined,
    maxLength: null,
    // min: undefined,
    name: '',
    onChange: null,
    onClosed: null,
    onContentReady: null,
    onCopy: null,
    onCut: null,
    onDisposing: null,
    onEnterKey: null,
    onFocusIn: null,
    onFocusOut: null,
    onInitialized: null,
    onInput: null,
    onKeyDown: null,
    onKeyUp: null,
    onOpened: null,
    onOptionChanged: null,
    onPaste: null,
    onValueChanged: null,
    opened: false,
    openOnFieldClick: false,
    pickerType: 'calendar', // 'calendar', 'native' (iOS, Android), 'rollers' (Android below version 4.4, mobile devices)
    placeholder: '',
    readOnly: false,
    rtlEnabled: false,
    showAnalogClock: true,
    showClearButton: false,
    showDropDownButton: true,
    spellcheck: false,
    stylingMode: 'outlined',
    tabIndex: 0,
    type: 'date',
    useMaskBehavior: false,
    // validationError: undefined,
    // validationErrors: undefined,
    validationMessageMode: 'auto',
    validationStatus: 'valid',
    value: null,
    valueChangeEvent: 'change',
    visible: true,
    width: undefined
};
